import { FlexColumn, FlexRow, MaterialIcon } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { LabelXSmall } from 'baseui/typography';
import moment from 'moment';
import 'moment/locale/es';
import { memo } from 'react';
import { generatePath } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { FOLLOW_ACTION, TRACKING_PAGE } from '../../constants/app';
import { useCamera } from '../../hooks/useCamera';
import { useStyles } from '../../hooks/useStyles';
import { apiParamsState, availableActionsState, isPublicPageState } from '../../storage/app';
import { selectedCurrentStateActionState } from '../../storage/client';
import { selectedDeliveryFenceIdState, selectedFenceIdState } from '../../storage/map';
import { CurrentState, CurrentStateAction } from '../../types/currentState';
import { deviceIsCamera } from '../../utils/currentStates';
import ConsumptionLabel from '../shared/ConsumptionLabel';
import ShareAssetsButton from '../shared/ShareAssetsButton';
import { StyledTooltip } from '../ui/StyledTooltip';
import LastCurrentStateReport from './LastCurrentStateReport';

type Props = Readonly<{
  currentState: CurrentState;
}>;

function CurrentStateCardFooter({ currentState }: Props) {
  const { css, theme, classes, concatenateClasses } = useStyles();
  const { zoomTo } = useCamera();
  moment.locale('es');
  const apiParams = useRecoilValue(apiParamsState);
  const actions = useRecoilValue(availableActionsState);
  const isCamera = deviceIsCamera(currentState);
  const setAction = useSetRecoilState(selectedCurrentStateActionState);
  const isPublicPage = useRecoilValue(isPublicPageState);
  const setSelectedFenceId = useSetRecoilState(selectedFenceIdState);
  const setSelectedDeliveryFenceId = useSetRecoilState(selectedDeliveryFenceIdState);

  const lng = currentState.point.coordinates[0];
  const lat = currentState.point.coordinates[1];

  const { deliveryFence, geoFence } = currentState.asset;

  return (
    <FlexRow
      classNames={concatenateClasses(
        classes.centeredBetween,
        css({
          padding: `${theme.sizing.scale200} ${theme.sizing.scale600}`,
        }),
      )}
    >
      <LastCurrentStateReport timestamp={currentState.timestamp} />
      <FlexRow
        gap={theme.sizing.scale300}
        centered
      >
        {(deliveryFence || geoFence) && (
          <StyledTooltip
            content={
              <FlexColumn gap={0}>
                {deliveryFence && (
                  <LabelXSmall
                    color={theme.colors.buttonPrimaryText}
                    margin={0}
                  >
                    {deliveryFence && `P. Entrega: ${deliveryFence.name}`}
                  </LabelXSmall>
                )}
                {geoFence && (
                  <LabelXSmall
                    color={theme.colors.buttonPrimaryText}
                    margin={0}
                  >
                    {geoFence && `Cerco: ${geoFence.name}`}
                  </LabelXSmall>
                )}
              </FlexColumn>
            }
            showArrow={false}
            placement="bottom"
            ignoreBoundary
          >
            <Button
              shape="circle"
              kind="tertiary"
              size="mini"
              onClick={() => {
                if (deliveryFence) {
                  setSelectedDeliveryFenceId(deliveryFence._id);
                } else if (geoFence) {
                  setSelectedFenceId(geoFence._id);
                }
              }}
            >
              <MaterialIcon
                name="in_fence"
                color="#4CAF50"
                size={theme.sizing.scale800}
              />
            </Button>
          </StyledTooltip>
        )}
        {isCamera && (
          <ConsumptionLabel
            gbConsumed={currentState.asset.gbConsumed}
            percentageConsumed={currentState.asset.gbAssetConsumption}
          />
        )}
        <FlexRow
          gap={theme.sizing.scale0}
          centered
        >
          <ShareAssetsButton currentState={currentState} />
          {actions.includes(FOLLOW_ACTION) && (
            <StyledTooltip
              content="Abrir seguimiento"
              showArrow={false}
              placement="bottom"
              ignoreBoundary
            >
              <Button
                shape="circle"
                kind="tertiary"
                size="mini"
                onClick={() =>
                  apiParams &&
                  window.open(
                    generatePath(TRACKING_PAGE.route, { id: apiParams.subClientId, assetId: currentState.asset._id }),
                    '_blank',
                    'directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no, height=700px, width=1400px',
                  )
                }
              >
                <MaterialIcon
                  name="open_in_new"
                  size="18px"
                />
              </Button>
            </StyledTooltip>
          )}
          {isPublicPage && (
            <StyledTooltip
              content="Información del activo"
              showArrow={false}
              placement="bottom"
              ignoreBoundary
            >
              <Button
                shape="circle"
                kind="tertiary"
                size="mini"
                onClick={() => setAction({ ident: currentState.ident, name: CurrentStateAction.info })}
              >
                <MaterialIcon
                  name="info"
                  size="18px"
                />
              </Button>
            </StyledTooltip>
          )}
          <StyledTooltip
            content="Encontrar en el mapa"
            showArrow={false}
            placement="bottom"
            ignoreBoundary
          >
            <Button
              shape="circle"
              kind="tertiary"
              size="mini"
              onClick={() => zoomTo({ lat, lng })}
            >
              <MaterialIcon
                name="location_searching"
                size="18px"
              />
            </Button>
          </StyledTooltip>
        </FlexRow>
      </FlexRow>
    </FlexRow>
  );
}

export default memo(CurrentStateCardFooter);
